import { call, put, select, takeLatest } from 'redux-saga/effects';
import * as notificationsApi from '../api/notificationsApi.js';
import {
  notificationsRequested,
  notificationsLoaded,
  notificationsLoadFailed,
} from './notificationsSlice.js';
import { toastShown } from '../../system/store/toastSlice.js';

function isOk(res) {
  return res && res.status && res.code === 200;
}

// The API returns data.notifications as an object — either empty `{}` for no
// notifications or keyed by notification id `{1: {...}, 2: {...}}` when
// populated. Coerce to an array at this boundary so downstream selectors and
// components see a uniform shape. Matches legacy iteration via Object.keys()
// in apps/legacy/src/components/notifications/index.js.
//
// Crucially, we preserve the server key as `id` on each entry. Using
// Object.values() discards those keys and forces selectors to derive an id
// from array index, which breaks dismiss-after-refetch when the server returns
// items in a different order (audit C8).
function toArray(raw) {
  if (Array.isArray(raw)) {
    return raw.map((n, i) => ({ id: String(n.id ?? n._id ?? i), ...n }));
  }
  if (raw && typeof raw === 'object') {
    return Object.entries(raw).map(([id, value]) => ({ id, ...value }));
  }
  return [];
}

export function* handleNotificationsRequested() {
  const priorStatus = yield select((s) => s.notifications.status);
  try {
    const res = yield call(notificationsApi.fetchNotifications);
    if (!isOk(res)) {
      yield put(notificationsLoadFailed(res?.message ?? 'Failed to load notifications'));
      return;
    }
    yield put(notificationsLoaded(toArray(res.data?.notifications)));
    if (priorStatus === 'failed') {
      yield put(toastShown({ variant: 'info', message: 'Notifications updated' }));
    }
  } catch (err) {
    yield put(notificationsLoadFailed(err?.message ?? 'Network error'));
  }
}

export default function* notificationsSaga() {
  yield takeLatest(notificationsRequested.type, handleNotificationsRequested);
}
